"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import { FaArrowRight, FaCheck } from "react-icons/fa6";

import WebsiteAuditRequestPopup, {
  getWebsiteUrlError,
  isValidWebsiteUrl,
} from "@/components/common/WebsiteAuditRequestPopup";
import GradientButton from "@/components/ui/GradientButton";
import Input from "@/components/ui/Input";

const benefits = [
  "Free automated scan",
  "Detailed report with findings",
  "Expert recommendations",
];

const auditFindings = [
  "Low contrast text",
  "Missing image alt text",
  "Form label missing",
  "Heading structure",
  "Keyboard navigation",
];

const findingColors = [
  "bg-rose-600",
  "bg-orange-500",
  "bg-pink-300",
  "bg-violet-300",
  "bg-violet-600",
];

const findingMarkers = ["!", "!", "!", "H", "⌨"];

const ease = [0.22, 1, 0.36, 1] as const;

export default function AdaComplianceHero() {
  const reduceMotion = useReducedMotion();
  const [displayedScore, setDisplayedScore] = useState(0);
  const [website, setWebsite] = useState("");
  const [websiteError, setWebsiteError] = useState("");
  const [isAuditPopupOpen, setIsAuditPopupOpen] = useState(false);

  const openAuditPopup = () => {
    const error = getWebsiteUrlError(website);
    setWebsiteError(error);
    if (!error) setIsAuditPopupOpen(true);
  };

  useEffect(() => {
    if (reduceMotion) {
      const frameId = window.requestAnimationFrame(() => setDisplayedScore(83));
      return () => window.cancelAnimationFrame(frameId);
    }

    let frameId = 0;
    const startTimeout = window.setTimeout(() => {
      const startTime = performance.now();
      const duration = 1500;

      const updateScore = (now: number) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        setDisplayedScore(Math.round(easedProgress * 83));

        if (progress < 1) {
          frameId = window.requestAnimationFrame(updateScore);
        }
      };

      frameId = window.requestAnimationFrame(updateScore);
    }, 800);

    return () => {
      window.clearTimeout(startTimeout);
      window.cancelAnimationFrame(frameId);
    };
  }, [reduceMotion]);

  return (
    <section
      id="top"
      aria-labelledby="ada-hero-title"
      className="overflow-hidden bg-[linear-gradient(180deg,#fdf2f8_0%,#fdf4ff_50%,#ffffff_100%)] px-4 pb-12 pt-12 transition-colors duration-300 sm:px-6 sm:pb-16 sm:pt-16 lg:px-8 dark:bg-gray-950 dark:bg-none"
    >
      <div className="mx-auto grid w-full max-w-7xl items-center gap-8 lg:grid-cols-2 lg:gap-10 xl:gap-16">
        <motion.div
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: reduceMotion ? 0 : 0.85,
            delay: reduceMotion ? 0 : 0.05,
            ease,
          }}
          className="flex flex-col items-start gap-5"
        >
          <p className="font-poppins text-sm font-bold uppercase tracking-widest text-pink-700 dark:text-pink-400">
            ADA Website Accessibility Audit
          </p>
          <h1
            id="ada-hero-title"
            className="font-poppins text-4xl font-bold leading-tight tracking-tight text-indigo-950 dark:text-white sm:text-5xl lg:text-5xl"
          >
            Make Your Website Accessible to{" "}
            <span className="text-pink-600 dark:text-pink-400">Everyone</span>
          </h1>
          <p className="max-w-2xl text-base leading-relaxed text-slate-600 dark:text-slate-300 sm:text-lg">
            Get a free preliminary ADA (WCAG) accessibility scan for your
            website and a detailed report with practical next steps. We help
            U.S. businesses find and fix accessibility issues to improve the
            user experience and reduce legal risk.
          </p>

          <ul className="grid gap-3 text-sm font-medium text-indigo-950 dark:text-slate-100 sm:text-base">
            {benefits.map((benefit) => (
              <li key={benefit} className="flex items-center gap-3">
                <span className="grid size-6 shrink-0 place-items-center rounded-full bg-pink-600 text-white">
                  <FaCheck aria-hidden className="text-xs" />
                </span>
                {benefit}
              </li>
            ))}
          </ul>

          <div className="mt-2 w-full max-w-2xl space-y-2">
            <form
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                openAuditPopup();
              }}
              className="flex flex-col gap-3 sm:flex-row sm:items-start"
            >
              <div className="min-w-0 flex-1">
                <Input
                  id="ada-url"
                  name="website"
                  type="url"
                  label="Your website URL"
                  labelClassName="sr-only !mb-0"
                  placeholder="https://yourwebsite.com"
                  value={website}
                  error={websiteError}
                  reserveErrorSpace={false}
                  onChange={(event) => {
                    const value = event.target.value;
                    setWebsite(value);
                    if (isValidWebsiteUrl(value)) setWebsiteError("");
                  }}
                  className="!h-14 !w-full !rounded-xl !border !border-slate-200 !bg-white !px-5 !text-base !text-indigo-950 placeholder:!text-slate-400 focus:!border-pink-400 focus:!ring-2 focus:!ring-pink-200 dark:!border-slate-700 dark:!bg-slate-900 dark:!text-white dark:placeholder:!text-slate-500 dark:focus:!ring-pink-900"
                />
              </div>
              <GradientButton
                type="submit"
                variant="peach-pink"
                className="h-auto min-h-14 shrink-0 px-6 font-semibold"
              >
                Get Free Scan <FaArrowRight aria-hidden />
              </GradientButton>
            </form>
          </div>
          {/* <p className="text-sm text-slate-500 dark:text-slate-400">
            No credit card required. Get your report in 1–2 business days.
          </p> */}
        </motion.div>

        <motion.div
          aria-hidden="true"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: reduceMotion ? 0 : 0.9,
            delay: reduceMotion ? 0 : 0.2,
            ease,
          }}
          className="relative mx-auto w-full max-w-2xl px-3 pb-12 pt-8 sm:px-8 sm:pb-16"
        >
          <div
            className="overflow-hidden rounded-2xl bg-slate-950 shadow-2xl shadow-indigo-950/20"
          >
            <div className="flex items-center justify-between bg-slate-900 px-4 py-3">
              <span className="font-poppins text-sm font-bold tracking-wide text-amber-100">
                SAVORA
              </span>
              <div className="flex gap-2">
                <span className="h-1.5 w-6 rounded-full bg-white/40" />
                <span className="h-1.5 w-6 rounded-full bg-white/40" />
                <span className="h-1.5 w-6 rounded-full bg-white/40" />
              </div>
            </div>
            <div className="relative grid min-h-64 grid-cols-2 overflow-hidden sm:min-h-80">
              <div className="flex flex-col justify-center gap-4 p-5 sm:p-8">
                <p className="font-poppins text-xl font-semibold leading-tight text-white sm:text-3xl">
                  Great Food Brings People Together
                </p>
                <span className="h-2 w-11/12 rounded-full bg-white/25" />
                <span className="h-2 w-3/4 rounded-full bg-white/25" />
                <span className="mt-1 h-8 w-24 rounded-md bg-amber-600" />
              </div>
              <div className="flex items-end bg-linear-to-br from-amber-950 via-amber-800 to-amber-500 p-4">
                <div className="w-full space-y-3 rounded-xl bg-black/20 p-4">
                  <span className="block h-3 w-3/4 rounded-full bg-white/70" />
                  <span className="block h-3 w-1/2 rounded-full bg-white/50" />
                  <span className="block h-20 rounded-lg bg-white/15" />
                </div>
              </div>
              {!reduceMotion && (
                <motion.span
                  aria-hidden="true"
                  initial={{ y: "-100%" }}
                  animate={{ y: "300%" }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  className="pointer-events-none absolute inset-x-0 top-0 h-1/3 border-b-2 border-pink-300/80 bg-linear-to-b from-transparent to-pink-600/20"
                />
              )}
              {!reduceMotion &&
                [
                  "left-4 top-6 bg-rose-600",
                  "right-1/4 top-1/2 bg-orange-500",
                  "bottom-10 left-1/3 bg-pink-600",
                ].map((marker, index) => (
                  <motion.span
                    key={marker}
                    aria-hidden="true"
                    animate={{ scale: [1, 2.8], opacity: [0.65, 0] }}
                    transition={{
                      duration: 1.8,
                      delay: index * 0.6,
                      repeat: Infinity,
                      ease: "easeOut",
                    }}
                    className={`absolute size-3 rounded-full ring-4 ring-white ${marker}`}
                  />
                ))}
              <span className="absolute bottom-2 left-3 inline-flex items-center gap-2 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-indigo-950">
                <span className="size-3 rounded-full border-2 border-pink-200 border-t-pink-600 motion-safe:animate-spin motion-reduce:animate-none" />
                Scanning page...
              </span>
            </div>
          </div>

          <motion.div
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: reduceMotion ? 0 : 0.65,
              delay: reduceMotion ? 0 : 0.3,
              ease,
            }}
            className="absolute right-0 top-0 hidden flex-col items-center rounded-2xl bg-white p-3 shadow-xl shadow-indigo-950/15 dark:bg-slate-900 sm:flex sm:right-2 sm:p-4"
          >
              <div className="relative h-14 w-28">
                <svg
                  viewBox="0 0 120 70"
                  className="absolute inset-0 h-14 w-28"
                  aria-hidden="true"
                >
                <defs>
                  <linearGradient
                    id="ada-score-gradient"
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="0"
                  >
                    <stop offset="0" stopColor="#f7845a" />
                    <stop offset="0.55" stopColor="#e11d63" />
                    <stop offset="1" stopColor="#7b3fe4" />
                  </linearGradient>
                </defs>
                <path
                  d="M10 60a50 50 0 0 1 100 0"
                  fill="none"
                  stroke="#f1f2f7"
                  strokeWidth="10"
                  strokeLinecap="round"
                />
                <motion.path
                  d="M10 60a50 50 0 0 1 100 0"
                  fill="none"
                  stroke="url(#ada-score-gradient)"
                  strokeWidth="10"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 0.83 }}
                  transition={{
                    duration: reduceMotion ? 0 : 1.5,
                    delay: reduceMotion ? 0 : 0.8,
                    ease,
                  }}
                />
                </svg>
                <motion.span className="absolute inset-x-0 top-6 text-center font-poppins text-2xl font-bold leading-7 text-indigo-950 dark:text-white">
                  {displayedScore}
                </motion.span>
              </div>
              <span className="text-center text-xs font-medium text-slate-600 dark:text-slate-300">
              Accessibility score
            </span>
          </motion.div>

          <motion.div
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: reduceMotion ? 0 : 0.65,
              delay: reduceMotion ? 0 : 0.3,
              ease,
            }}
            className="absolute left-0 top-12 hidden space-y-3 rounded-2xl bg-white p-4 shadow-xl shadow-indigo-950/15 dark:bg-slate-900 sm:block sm:top-16 sm:p-5"
          >
            <p className="font-poppins text-sm font-semibold text-indigo-950 dark:text-white">
              Accessibility findings
            </p>
            {auditFindings.map((finding, index) => (
              <motion.div
                key={finding}
                initial={reduceMotion ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  duration: reduceMotion ? 0 : 0.5,
                  delay: reduceMotion ? 0 : 0.5 + index * 0.04,
                  ease,
                }}
                className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 sm:text-sm"
              >
                <span
                  className={`grid size-4 shrink-0 place-items-center rounded-full text-xs font-bold text-white ${findingColors[index]}`}
                >
                  {findingMarkers[index]}
                </span>
                {finding}
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: reduceMotion ? 0 : 0.65,
              delay: reduceMotion ? 0 : 0.3,
              ease,
            }}
            className="absolute bottom-0 right-0 hidden items-start gap-3 rounded-2xl bg-white p-4 shadow-xl shadow-indigo-950/15 dark:bg-slate-900 sm:flex sm:p-5"
          >
            <span className="grid h-14 w-12 shrink-0 place-items-center rounded-lg bg-linear-to-br from-orange-400 to-pink-600 font-poppins text-sm font-bold text-white">
              PDF
            </span>
            <div className="space-y-2">
              <p className="font-poppins text-sm font-bold text-indigo-950 dark:text-white">
                ADA Accessibility Report
              </p>
              {["Issues identified", "Priority recommendations", "Expert review"].map(
                (item) => (
                  <p key={item} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                    <span className="size-2 rounded-full bg-pink-600" />
                    {item}
                  </p>
                ),
              )}
            </div>
          </motion.div>
        </motion.div>
      </div>
      <WebsiteAuditRequestPopup
        isOpen={isAuditPopupOpen}
        website={website}
        title="Get Your Free Accessibility Scan"
        description="Share your website and email to request your free scan."
        requestName="ADA Compliance Audit Request"
        onWebsiteChange={setWebsite}
        onOpenChange={setIsAuditPopupOpen}
      />
    </section>
  );
}
